/**
 * IndexedDB queue for signed returns (spec §12.3): signature and capture
 * timestamp are fixed at capture; the queue survives connectivity loss and
 * app restarts, and syncs when the network returns.
 */
import type { SignedArtefact } from "@vra/signing";

const DB_NAME = "vra-capture";
const DB_VERSION = 1;
const QUEUE = "queue";
const KEYS = "keys";

export interface QueuedReturn {
  id?: number;
  signed: SignedArtefact;
  queuedAt: string;
  syncState: "PENDING" | "SYNCED" | "REJECTED";
  serverResponse?: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE)) {
        db.createObjectStore(QUEUE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(KEYS)) {
        db.createObjectStore(KEYS);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export function enqueue(item: QueuedReturn): Promise<IDBValidKey> {
  return tx(QUEUE, "readwrite", (s) => s.add(item));
}

export function listQueue(): Promise<QueuedReturn[]> {
  return tx(QUEUE, "readonly", (s) => s.getAll() as IDBRequest<QueuedReturn[]>);
}

export function updateQueued(item: QueuedReturn): Promise<IDBValidKey> {
  return tx(QUEUE, "readwrite", (s) => s.put(item));
}

export function getStoredValue<T>(key: string): Promise<T | undefined> {
  return tx(KEYS, "readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
}

export function setStoredValue<T>(key: string, value: T): Promise<IDBValidKey> {
  return tx(KEYS, "readwrite", (s) => s.put(value as never, key));
}
